import React, { useState } from 'react'
import { Button } from './ui/Button'
import { Input } from './ui/Input'
import { submitPreRegistration, PreRegistrationData } from '../services/preregistration'
import { brandColors, semanticColors, typography, spacing, borderRadius, shadows } from '../theme'

interface PreRegistrationFormProps {
  onSuccess?: () => void
  variant?: 'inline' | 'modal'
}

export const PreRegistrationForm: React.FC<PreRegistrationFormProps> = ({
  onSuccess,
  variant = 'inline',
}) => {
  const [formData, setFormData] = useState<PreRegistrationData>({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    role: '',
    useCase: undefined,
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    setError(null)
  }

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.email) {
      setError('Email is required')
      return
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsSubmitting(true)

    try {
      await submitPreRegistration(formData)
      setSuccess(true)
      if (onSuccess) {
        onSuccess()
      }
      // Reset form after success
      setTimeout(() => {
        setFormData({
          email: '',
          firstName: '',
          lastName: '',
          company: '',
          role: '',
          useCase: undefined,
          message: '',
        })
        setSuccess(false)
      }, 3000)
    } catch (err: unknown) {
      const errorMessage =
        err && typeof err === 'object' && 'message' in err
          ? String(err.message)
          : 'Failed to submit pre-registration. Please try again.'
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <div
        style={{
          padding: spacing[8],
          backgroundColor: semanticColors.success,
          color: 'white',
          borderRadius: borderRadius.lg,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: '2rem', marginBottom: spacing[4] }}>✓</div>
        <h3
          style={{
            fontSize: typography.fontSize.xl,
            fontWeight: typography.fontWeight.semibold,
            marginBottom: spacing[2],
          }}
        >
          Thank you for your interest!
        </h3>
        <p style={{ fontSize: typography.fontSize.base }}>
          We'll notify you as soon as SovereignScope is available.
        </p>
      </div>
    )
  }

  const containerStyle: React.CSSProperties =
    variant === 'modal'
      ? {
          padding: spacing[8],
          backgroundColor: brandColors.surface,
          borderRadius: borderRadius.xl,
          boxShadow: shadows.xl,
          maxWidth: '500px',
          width: '100%',
        }
      : {
          padding: spacing[8],
          backgroundColor: brandColors.surface,
          borderRadius: borderRadius.xl,
          boxShadow: shadows.lg,
        }

  return (
    <form onSubmit={handleSubmit} style={containerStyle}>
      <h3
        style={{
          fontSize: typography.fontSize['2xl'],
          fontWeight: typography.fontWeight.bold,
          color: brandColors.textPrimary,
          marginBottom: spacing[4],
        }}
      >
        Get Early Access
      </h3>
      <p
        style={{
          fontSize: typography.fontSize.base,
          color: brandColors.textSecondary,
          marginBottom: spacing[6],
        }}
      >
        Be among the first to transform your procurement process with AI-powered
        digital sovereignty assessment.
      </p>

      {error && (
        <div
          style={{
            padding: spacing[4],
            backgroundColor: '#FEE2E2',
            color: semanticColors.error,
            borderRadius: borderRadius.md,
            marginBottom: spacing[4],
            fontSize: typography.fontSize.sm,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
        <div>
          <label
            htmlFor="email"
            style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: brandColors.textPrimary,
              marginBottom: spacing[2],
            }}
          >
            Email Address <span style={{ color: semanticColors.error }}>*</span>
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="you@company.com"
            required
            error={error && !formData.email ? error : undefined}
            style={{
              width: '100%',
            }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing[4] }}>
          <div>
            <label
              htmlFor="firstName"
              style={{
                display: 'block',
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                color: brandColors.textPrimary,
                marginBottom: spacing[2],
              }}
            >
              First Name
            </label>
            <Input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="John"
            />
          </div>
          <div>
            <label
              htmlFor="lastName"
              style={{
                display: 'block',
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                color: brandColors.textPrimary,
                marginBottom: spacing[2],
              }}
            >
              Last Name
            </label>
            <Input
              id="lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Doe"
            />
          </div>
        </div>

        <div>
          <label
            htmlFor="company"
            style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: brandColors.textPrimary,
              marginBottom: spacing[2],
            }}
          >
            Company
          </label>
          <Input
            id="company"
            name="company"
            type="text"
            value={formData.company}
            onChange={handleChange}
            placeholder="Your Company"
          />
        </div>

        <div>
          <label
            htmlFor="role"
            style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: brandColors.textPrimary,
              marginBottom: spacing[2],
            }}
          >
            Role
          </label>
          <Input
            id="role"
            name="role"
            type="text"
            value={formData.role}
            onChange={handleChange}
            placeholder="e.g., Procurement Director, CTO"
          />
        </div>

        <div>
          <label
            htmlFor="useCase"
            style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: brandColors.textPrimary,
              marginBottom: spacing[2],
            }}
          >
            Primary Use Case
          </label>
          <select
            id="useCase"
            name="useCase"
            value={formData.useCase || ''}
            onChange={handleChange}
            style={{
              width: '100%',
              padding: `${spacing[2]} ${spacing[3]}`,
              border: '1px solid #CBD5E1',
              borderRadius: borderRadius.md,
              fontSize: typography.fontSize.base,
              fontFamily: typography.fontFamily.primary,
              backgroundColor: 'white',
              color: brandColors.textPrimary,
            }}
          >
            <option value="">Select a use case</option>
            <option value="procurement">Procurement & Vendor Assessment</option>
            <option value="vc">VC Due Diligence</option>
            <option value="m&a">M&A Technology Assessment</option>
            <option value="cto">Internal Tech Stack Governance</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label
            htmlFor="message"
            style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              fontWeight: typography.fontWeight.medium,
              color: brandColors.textPrimary,
              marginBottom: spacing[2],
            }}
          >
            Additional Information (Optional)
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your specific needs..."
            rows={4}
            style={{
              width: '100%',
              padding: spacing[3],
              border: '1px solid #CBD5E1',
              borderRadius: borderRadius.md,
              fontSize: typography.fontSize.base,
              fontFamily: typography.fontFamily.primary,
              resize: 'vertical',
            }}
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          loading={isSubmitting}
          style={{
            width: '100%',
            marginTop: spacing[2],
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Request Early Access'}
        </Button>

        <p
          style={{
            fontSize: typography.fontSize.xs,
            color: brandColors.textMuted,
            textAlign: 'center',
            marginTop: spacing[2],
          }}
        >
          By submitting, you agree to receive updates about SovereignScope. We
          respect your privacy and will never share your information.
        </p>
      </div>
    </form>
  )
}


